<template>
  <v2-container v-if="teams[$route.params.team].account">
    <v2-uppy-upload
      id="tempSettingsUppyModal"
      :locale="teams[$route.params.team].account.locale"
      :autoProceed="false"
      :autoOpenFileEditor="true"
      withCropper
      :openModal="uppyModalOpened"
      :addEvent="{
        event: 'dashboard:modal-closed',
        method: () => {
          toggleUppyModal(false);
        },
      }"
      :trigger="null"
      :create-file="createFile"
      :uploadConfig="uploadConfig"
      :cropperConfig="cropperConfig"
      @completed="
        (data) => {
          completedHandler(data, currentSettingUid);
        }
      "
    ></v2-uppy-upload>
    <v2-delete-modal
      name="delete-snippets-modal"
      :title="
        $t('modals.deleteTemplateSnippet.title', {
          name: $h.getNameFromSnippetByUid(currentDeleteSnippetUid, currentTemplateSnippets),
        })
      "
      :text="$t('modals.deleteTemplateSnippet.text')"
      :deleteHandler="
        () => {
          deleteModalHandler(currentDeleteSnippetUid);
        }
      "
    >
    </v2-delete-modal>
    <v2-row>
      <v2-col col12>
        <v-breadcrumb :items="breadcrumbTemplateItems" :mobile="breadcrumbMobileItem"></v-breadcrumb>
      </v2-col>
    </v2-row>
    <v2-row>
      <v2-col col12>
        <div class="w-full flex flex-wrap justify-between">
          <div v-if="currentTemplate">
            <h3 class="heading-3">{{ currentTemplate.name }}</h3>
            <div class="flex items-center">
              <span
                class="rounded-full mr-1"
                style="width: 10px; height: 10px"
                :class="[templateStatus === 'published' ? 'bg-v2-success-500' : 'bg-v2-error-500']"
              ></span>
              <span class="paragraph-3 text-v2-neutral-500">{{
                $t("settings.templates.status." + templateStatus)
              }}</span>
            </div>
          </div>
          <div class="w-full md:w-auto mt-4 md:mt-0 flex">
            <v2-button
              v2Secondary
              :fullWidth="$windowWidth < 450"
              :class="{ 'mb-2': $windowWidth < 450 }"
              md
              class="mr-2"
              v-if="currentTemplate && !currentTemplate.template_builder_disabled"
              @click.native="openTemplateBuilder(currentTemplate.uid)"
              ><v2-icon
                class="mr-1"
                :data="require('@icon/Icon=edit-2.svg')"
                color="inherit"
                width="auto"
                height="16"
              ></v2-icon
              >{{ $t("actions.editTemplate") }}</v2-button
            >
            <v2-button
              v2Secondary
              :fullWidth="$windowWidth < 450"
              :class="{ 'mb-2': $windowWidth < 450 }"
              md
              class="mr-2"
              @click.native="openPreview"
              ><v2-icon
                class="mr-1"
                :data="require('@icon/Icon=eye.svg')"
                color="inherit"
                width="auto"
                height="18"
              ></v2-icon
              >{{ $t("settings.templates.actions.preview") }}</v2-button
            >
            <v2-button
              v2Secondary
              :fullWidth="$windowWidth < 450"
              :class="{ 'mb-2': $windowWidth < 450 }"
              :active="!templateIsResetting && !templateIsPublishing"
              md
              class="mr-2 relative"
              @click.native.stop="$eventbus.$emit('toggleDropdown', 'reset')"
              ><v2-icon
                class="mr-1"
                :data="require('@icon/Icon=rotate-ccw.svg')"
                color="inherit"
                width="auto"
                height="16"
              ></v2-icon
              >{{ $t("settings.templates.actions.reset") }}
              <v2-dropdown dropdownUid="reset" top="50px" right="0px" withArrow arrowRight>
                <div>
                  <label class="mb-0 paragraph-2 text-v2-neutral-800 block text-left">{{
                    $t("settings.templates.actions.reset")
                  }}</label>
                  <p class="paragraph-3 text-v2-neutral-500 flex items-center h-full">
                    {{ $t("settings.templates.texts.reset") }}
                  </p>
                </div>
                <hr />
                <div class="mt-4 flex">
                  <v2-button fullWidth v2Secondary md @click.native.stop="$eventbus.$emit('toggleDropdown', 'reset')">{{
                    $t("actions.cancel")
                  }}</v2-button>
                  <v2-button fullWidth v2Primary md @click.native="undoTemplateDraftChanges" class="ml-4"
                    ><v2-icon
                      class="mr-1"
                      :data="require('@icon/Icon=rotate-ccw.svg')"
                      color="inherit"
                      width="auto"
                      height="16"
                    ></v2-icon
                    >{{ $t("settings.templates.actions.reset") }}</v2-button
                  >
                </div>
              </v2-dropdown>
            </v2-button>
            <v2-button
              class="relative"
              v2Primary
              :fullWidth="$windowWidth < 450"
              :active="
                !templateIsResetting && !templateIsPublishing && !teamsSettings[$route.params.team].unsavedChanges
              "
              md
              @click.native.stop="$eventbus.$emit('toggleDropdown', 'publish')"
              ><v2-icon
                class="mr-1"
                :data="require('@icon/Icon=share.svg')"
                color="inherit"
                width="auto"
                height="16"
              ></v2-icon
              >{{ $t("settings.templates.actions.publish") }}
              <v2-dropdown dropdownUid="publish" top="50px" right="0px" withArrow arrowRight>
                <div>
                  <label class="mb-0 paragraph-2 text-v2-neutral-800 block text-left">{{
                    $t("settings.templates.actions.publish")
                  }}</label>
                  <p class="paragraph-3 text-v2-neutral-500 flex items-center h-full">
                    {{ $t("settings.templates.texts.publish") }}
                  </p>
                </div>
                <hr />
                <div class="mt-4 flex">
                  <v2-button
                    fullWidth
                    v2Secondary
                    md
                    @click.native.stop="$eventbus.$emit('toggleDropdown', 'publish')"
                    >{{ $t("actions.cancel") }}</v2-button
                  >
                  <v2-button fullWidth v2Primary md @click.native="publishTemplateDraft" class="ml-4"
                    ><v2-icon
                      class="mr-1"
                      :data="require('@icon/Icon=share.svg')"
                      color="inherit"
                      width="auto"
                      height="16"
                    ></v2-icon
                    >{{ $t("settings.templates.actions.publish") }}</v2-button
                  >
                </div>
              </v2-dropdown>
            </v2-button>
          </div>
        </div>
      </v2-col>
    </v2-row>
    <v2-row>
      <v2-col col12>
        <v-tabs
          ref="container"
          slotNameSlug
          :tabs="tabs"
          @setActiveTab="
            (slug) => {
              setActiveTab(slug);
            }
          "
          :active="getActiveTab()"
        >
          <div :slot="'_general'">
            <hr class="mb-8" />
            <div v-if="templateIsReady">
              <v2-row>
                <v2-col col12 noSpaceX>
                  <div class="flex flex-wrap justify-between">
                    <div class="w-full md:w-auto md:max-w-1/2">
                      <h4 class="heading-4">{{ $t("settings.templates.titles.general") }}</h4>
                      <p class="paragraph-2 text-v2-neutral-500">
                        {{ $t("settings.templates.texts.general") }}
                      </p>
                    </div>
                    <v2-button v2Secondary :fullWidth="$windowWidth < 450" @click.native="submitTemplate()">{{
                      $t("settings.templates.actions.save")
                    }}</v2-button>
                  </div>
                </v2-col>
              </v2-row>
              <v2-row>
                <v2-col col6 noSpace noSpaceX>
                  <v2-textfield
                    :label="$t('template.form.chooseName.label')"
                    :placeholder="$t('template.name')"
                    v-model="name"
                    :required="true"
                    v-validate="'required'"
                    name="name"
                    :error="errors.first('name')"
                    :data-vv-as="$t('template.name')"
                  />
                </v2-col>
                <v2-col col6 noSpaceX noSpace>
                  <!-- eslint-disable max-len -->
                  <div
                    class="paragraph-3 text-v2-neutral-500 flex items-center h-full md:pl-4 md:ml-4 md:border-l md:border-v2-neutral-300"
                  >
                    <!-- eslint-enable max-len -->
                    {{ $t("template.form.chooseName.note") }}
                  </div>
                </v2-col>
              </v2-row>
              <v2-row>
                <v2-col col6 noSpace noSpaceX>
                  <v2-select
                    :label="$t('template.form.chooseLang.label')"
                    :placeholder="$t('template.locale')"
                    v-model="locale"
                    :options="localeOptions"
                    optionLabel="label"
                    id="locale"
                    name="locale"
                    v-validate="'required'"
                    :error="errors.first('locale')"
                    :showLabels="false"
                    :data-vv-as="$t('template.locale')"
                    @select="
                      (selectedLocale) => {
                        preselectAvailableLanguage(selectedLocale);
                      }
                    "
                  ></v2-select>
                </v2-col>
                <v2-col col6 noSpaceX noSpace>
                  <!-- eslint-disable max-len -->
                  <p
                    class="paragraph-3 text-v2-neutral-500 flex items-center h-full md:pl-4 md:ml-4 md:border-l md:border-v2-neutral-300"
                  >
                    <!-- eslint-enable max-len -->
                    {{ $t("template.form.chooseLang.note") }}
                  </p>
                </v2-col>
              </v2-row>
              <v2-row>
                <v2-col col6 noSpaceX noSpace>
                  <v2-select
                    :label="$t('template.form.chooseAvailableLangs.label')"
                    :placeholder="$t('template.languages')"
                    v-model="languages"
                    :options="languageOptions"
                    v-validate="'required'"
                    :error="errors.first('languages')"
                    optionLabel="label"
                    trackBy="label"
                    id="languages"
                    name="languages"
                    :multiple="true"
                    :taggable="false"
                    :hideSelected="true"
                    :closeOnSelect="false"
                    :clearOnSelect="false"
                    :data-vv-as="$t('template.languages')"
                  ></v2-select>
                </v2-col>
                <v2-col col6 noSpaceX noSpace>
                  <!-- eslint-disable max-len -->
                  <div
                    class="paragraph-3 text-v2-neutral-500 flex items-center h-full md:pl-4 md:ml-4 md:border-l md:border-v2-neutral-300"
                  >
                    <!-- eslint-enable max-len -->
                    {{ $t("template.form.chooseAvailableLangs.note") }}
                  </div>
                </v2-col>
              </v2-row>
            </div>
            <v-spinner :loading="!templateIsReady" class="mt-100px" style="z-index: 9999"></v-spinner>
          </div>
          <div :slot="$t('menus.textTemplates').replace(/\s+/g, '-').toLowerCase()">
            <hr class="mb-8" />
            <div v-if="templateIsReady">
              <v2-row v-if="currentTemplateSnippets && teamsSettings[$route.params.team]">
                <v2-col col12>
                  <v-list-header simple>
                    <v-list-header-cell :title="$t('titles.templateSnippets')" />
                    <v-list-header-cell
                      :link="$t('labels.newTemplateSnippet')"
                      :disabledLink="!teamCan('template-snippets')"
                      :disabledHandler="
                        () => {
                          $h.showSubscriptionErrorModal(
                            $modal,
                            teams[$route.params.team] && teams[$route.params.team].account.role.includes('owner')
                          );
                        }
                      "
                      :route="{
                        name: 'createTemplateSnippet',
                        params: {
                          template: currentTemplate.uid,
                        },
                      }"
                    />
                  </v-list-header>
                  <v-list-header complex>
                    <v-list-header-cell emptyIcon />
                    <v-list-header-cell
                      size="3"
                      :showAs="$t('templateSnippets.name')"
                      :showAsDropdownUid="$h.createUniqueUid()"
                      :showAsDropdown="sortMainColDropdown"
                    />
                    <v-list-header-cell
                      :showAs="
                        $t(teamsSettings[$route.params.team].listsSettings.templateSnippets.showAs.firstCol.label)
                      "
                      :size="teamsSettings[$route.params.team].listsSettings.templateSnippets.showAs.firstCol.size"
                      :showAsDropdownUid="$h.createUniqueUid()"
                      :showAsDropdown="showAsFirstColDropdown"
                    />
                    <v-list-header-cell
                      :showAs="
                        $t(teamsSettings[$route.params.team].listsSettings.templateSnippets.showAs.secondCol.label)
                      "
                      tabletHidden
                      :size="teamsSettings[$route.params.team].listsSettings.templateSnippets.showAs.secondCol.size"
                      :showAsDropdownUid="$h.createUniqueUid()"
                      :showAsDropdown="showAsSecondColDropdown"
                    />
                    <v-list-header-cell
                      :showAs="
                        $t(teamsSettings[$route.params.team].listsSettings.templateSnippets.showAs.thirdCol.label)
                      "
                      dropdownRight
                      :size="teamsSettings[$route.params.team].listsSettings.templateSnippets.showAs.thirdCol.size"
                      :showAsDropdownUid="$h.createUniqueUid()"
                      :showAsDropdown="showAsThirdColDropdown"
                    />
                    <v-list-header-cell
                      :mobile="$t('actions.sort')"
                      :mobileDropdownUid="$h.createUniqueUid()"
                      :mobileDropdown="mobileDropdown"
                    />
                    <v-list-header-cell
                      filter
                      dropdownRight
                      multiselect
                      :filterActive="teamsSettings[$route.params.team].listsSettings.templateSnippets.filterActive"
                      :filterDropdownUid="$h.createUniqueUid()"
                      :filterDropdown="filterDropdown"
                    />
                  </v-list-header>
                  <ul class="list">
                    <router-link
                      :to="{
                        name: 'TemplateSnippet',
                        params: {
                          template: currentTemplate.uid,
                          uid: snippet.uid,
                        },
                      }"
                      v-for="snippet in currentTemplateSnippets"
                      :key="snippet.uid"
                    >
                      <v-list-item complex hover>
                        <v-list-cell icon="Icon=folder.svg" iconFill iconColor="#5057AF" />
                        <v-list-cell :title="snippet.name" size="3" :mobileSubtitle="''" />
                        <v-list-cell
                          :size="teamsSettings[$route.params.team].listsSettings.templateSnippets.showAs.firstCol.size"
                          :value="
                            $h.formatValue(
                              teamsSettings[$route.params.team].listsSettings.templateSnippets.showAs.firstCol.format,
                              snippet[
                                teamsSettings[$route.params.team].listsSettings.templateSnippets.showAs.firstCol.key
                              ],
                              teams[$route.params.team].account.timezone
                            )
                          "
                        />
                        <v-list-cell
                          :size="teamsSettings[$route.params.team].listsSettings.templateSnippets.showAs.secondCol.size"
                          tabletHidden
                          :value="
                            $h.formatValue(
                              teamsSettings[$route.params.team].listsSettings.templateSnippets.showAs.secondCol.format,
                              snippet[
                                teamsSettings[$route.params.team].listsSettings.templateSnippets.showAs.secondCol.key
                              ],
                              teams[$route.params.team].account.timezone
                            )
                          "
                        />
                        <v-list-cell
                          :size="teamsSettings[$route.params.team].listsSettings.templateSnippets.showAs.thirdCol.size"
                          :value="
                            $h.formatValue(
                              teamsSettings[$route.params.team].listsSettings.templateSnippets.showAs.thirdCol.format,
                              snippet[
                                teamsSettings[$route.params.team].listsSettings.templateSnippets.showAs.thirdCol.key
                              ],
                              teams[$route.params.team].account.timezone
                            )
                          "
                        />
                        <v-list-cell
                          :style="$h.templateSnippetsStatus(snippet.status, 'default', false) ? 'opacity:' + 0 : ''"
                          iconDropdown
                          gray
                          icon="Icon=more-horizontal.svg"
                          iconColor="#5057af"
                          :dropdownData="moreDropdown"
                          :dropdownUid="$h.createUniqueUid()"
                          :dropdownValue="snippet"
                        />
                      </v-list-item>
                    </router-link>
                    <v-list-item v-if="!currentTemplateSnippets.length">
                      <v-list-cell :value="$t('placeholders.noTemplateSnippets')"></v-list-cell>
                    </v-list-item>
                  </ul>
                  <v-list-pagination
                    :currentPage="teamsSettings[$route.params.team].listsSettings.templateSnippets.pagination.page"
                    :showPerPage="teamsSettings[$route.params.team].listsSettings.templateSnippets.pagination.perPage"
                    :totalPages="teamsSettings[$route.params.team].listsSettings.templateSnippets.pagination.lastPage"
                    :apiCall="
                      (page, perPage) => {
                        changeTemplateSnippetsPage(page, perPage);
                      }
                    "
                  >
                  </v-list-pagination>
                </v2-col>
              </v2-row>
            </div>
            <v-spinner :loading="!templateIsReady" class="mt-100px" style="z-index: 9999"></v-spinner>
          </div>
          <div :slot="tab.replace(/\s+/g, '-').toLowerCase()" v-for="(tab, index) in tabs.slice(2)" :key="tab">
            <hr class="mb-8" />
            <div
              v-if="
                templateIsReady &&
                tab.replace(/\s+/g, '-').toLowerCase() === $route.params.tab &&
                currentSettings[index]
              "
            >
              <v2-row>
                <v2-col col12 noSpaceX>
                  <div class="flex flex-wrap justify-between">
                    <div class="w-full md:w-auto md:max-w-1/2">
                      <h4 class="heading-4">
                        {{ $h.getLanguageValue(currentSettings[index].title) }}
                      </h4>
                      <p
                        class="paragraph-2 text-v2-neutral-500"
                        v-html="$h.getLanguageValue(currentSettings[index].description)"
                      ></p>
                    </div>
                    <div class="flex flex-wrap w-full md:w-auto">
                      <v2-select
                        class="flex-shrink-0 mr-4"
                        :style="{ width: $windowWidth >= 450 ? '250px' : '100%' }"
                        :options="templateLocaleOptions"
                        optionLabel="label"
                        v-model="templateLocaleOption"
                        @input="initSettingValues()"
                      ></v2-select>
                      <v2-button
                        class="flex-shrink-0"
                        :fullWidth="$windowWidth < 450"
                        :fixedWidth="$windowWidth > 449"
                        v2Secondary
                        @click.native="save()"
                        >{{ $t("settings.templates.actions.save") }}</v2-button
                      >
                    </div>
                  </div>
                </v2-col>
              </v2-row>
              <div v-if="!currentSettings[index].with_subcategories">
                <div v-for="(setting, i) in currentSettings[index].category_settings" :key="setting.id">
                  <hr class="my-8" :class="{ hidden: i === 0 }" v-if="setting.type === 'label'" />
                  <v2-row>
                    <v2-col col6 noSpace noSpaceX v-if="setting.type === 'label'">
                      <label class="mb-0 paragraph-2 text-v2-neutral-800 block">
                        {{ $h.getLanguageValue(setting.title) }}
                      </label>
                      <p
                        v-if="$h.getLanguageValue(setting.description) !== ''"
                        class="paragraph-3 text-v2-neutral-500 flex items-center h-full"
                        v-html="$h.getLanguageValue(setting.description)"
                      ></p>
                    </v2-col>
                    <v2-col col6 noSpace noSpaceX v-if="setting.type === 'text'">
                      <v2-textfield
                        :iconSuffix="require('@icon/Icon=globe.svg')"
                        :label="$h.getLanguageValue(setting.title)"
                        @input="setUnsavedChanges({ val: true })"
                        v-if="setting.translatable"
                        v-model="settingValues[currentTemplateLocaleOption][setting.id]"
                        :placeholder="$h.getLanguageValue(setting.title)"
                      />
                      <v2-textfield
                        :label="$h.getLanguageValue(setting.title)"
                        @input="setUnsavedChanges({ val: true })"
                        v-if="!setting.translatable"
                        v-model="settingValues[defaultLocale][setting.id]"
                        :placeholder="$h.getLanguageValue(setting.title)"
                      />
                    </v2-col>
                    <v2-col col6 noSpace noSpaceX v-if="setting.type === 'url'">
                      <v2-textfield
                        :iconSuffix="require('@icon/Icon=globe.svg')"
                        :label="$h.getLanguageValue(setting.title)"
                        @input="setUnsavedChanges({ val: true })"
                        v-if="setting.translatable"
                        v-model="settingValues[currentTemplateLocaleOption][setting.id]"
                        :placeholder="$h.getLanguageValue(setting.title)"
                      />
                      <v2-textfield
                        :label="$h.getLanguageValue(setting.title)"
                        @input="setUnsavedChanges({ val: true })"
                        v-if="!setting.translatable"
                        v-model="settingValues[defaultLocale][setting.id]"
                        :placeholder="$h.getLanguageValue(setting.title)"
                      />
                    </v2-col>
                    <v2-col col6 noSpace noSpaceX v-if="setting.type === 'color'">
                      <v2-color-picker
                        :label="$h.getLanguageValue(setting.title)"
                        @update="updateColorValue"
                        :uid="setting.id"
                        :value="
                          colorValues.hasOwnProperty(setting.id) ? colorValues[setting.id] : 'rgba(255,255,255,1)'
                        "
                      ></v2-color-picker>
                    </v2-col>
                    <v2-col col6 noSpace noSpaceX v-if="setting.type === 'checkbox'">
                      <label class="mb-0 paragraph-2 text-v2-neutral-800 block">
                        {{ $h.getLanguageValue(setting.title) }}
                      </label>
                      <v-switch
                        @input="setUnsavedChanges({ val: true })"
                        name="switch"
                        :labels="{ true: '', false: '' }"
                        v-model="settingValues[defaultLocale][setting.id]"
                      ></v-switch>
                    </v2-col>
                    <v2-col col6 noSpace noSpaceX v-if="setting.type.indexOf('dropdown_') === 0">
                      <v2-select
                        v-if="dropdownValues.hasOwnProperty(setting.id)"
                        :label="$h.getLanguageValue(setting.title)"
                        :placeholder="''"
                        :customLabel="getTitle"
                        :multiple="false"
                        :value="dropdownValues[setting.id]"
                        :options="setting.options"
                        :id="'setting_id_' + setting.id"
                        :name="'setting_name_' + setting.id"
                        @select="
                          (option) => {
                            selectOption(option, setting.id);
                          }
                        "
                        :allowEmpty="false"
                        :searchable="false"
                        :closeOnSelect="true"
                        :clearOnSelect="false"
                        openDir="bottom"
                      >
                      </v2-select>
                    </v2-col>
                    <v2-col col12 noSpaceX v-if="setting.type === 'textarea'">
                      <label class="mb-0 paragraph-2 text-v2-neutral-800 block">{{
                        $h.getLanguageValue(setting.title)
                      }}</label>
                      <p
                        class="paragraph-3 text-v2-neutral-500 flex items-center h-full"
                        v-html="$h.getLanguageValue(setting.description)"
                      ></p>
                      <v2-textarea
                        tabIndent
                        rows="20"
                        @input="setUnsavedChanges({ val: true })"
                        v-model="settingValues[defaultLocale][setting.id]"
                        :class="{
                          textAreaPreformatted: setting.preformatted,
                        }"
                      ></v2-textarea>
                    </v2-col>
                    <v2-col col12 noSpaceX v-if="setting.type === 'radio'">
                      <label class="mb-0 paragraph-2 text-v2-neutral-800 block">{{
                        $h.getLanguageValue(setting.title)
                      }}</label>
                      <p
                        class="paragraph-3 text-v2-neutral-500 flex items-center h-full"
                        v-html="$h.getLanguageValue(setting.description)"
                      ></p>
                      <v2-radio-group
                        :name="setting.id"
                        :value="settingValues[defaultLocale][setting.id]"
                        v-model="settingValues[defaultLocale][setting.id]"
                        :options="formatRadioOptions(setting.options)"
                      ></v2-radio-group>
                    </v2-col>
                    <v2-col col12 noSpaceX v-if="setting.type === 'media'">
                      <label class="mb-0 paragraph-2 text-v2-neutral-800 block">{{
                        $h.getLanguageValue(setting.title)
                      }}</label>
                      <div class="flex flex-wrap">
                        <div
                          class="flex flex-shrink-0 relative mr-4 items-center border-v2-neutral-200 mb-4"
                          :class="{ border: !mediaValues[setting.id] || !mediaValues[setting.id].imageUrl }"
                          :style="{
                            width: setSettingWidth(setting),
                            height: setSettingHeight(setting),
                          }"
                        >
                          <v2-lazy-load
                            class="border border-v2-neutral-200"
                            v-if="mediaValues[setting.id]"
                            :isLoading="
                              mediaValues[setting.id] &&
                              mediaValues[setting.id].uid &&
                              !mediaValues[setting.id].imageUrl
                            "
                            :style="{
                              width: setSettingWidth(setting),
                              height: setSettingHeight(setting),
                            }"
                            :image="mediaValues[setting.id].imageUrl"
                          ></v2-lazy-load>
                        </div>
                        <div>
                          <div>
                            <v2-button v2Secondary sm @click.native="openUppyModal(setting)" class="mr-2"
                              ><v2-icon
                                :data="require('@icon/Icon=cloud.svg')"
                                fill
                                width="auto"
                                height="14px"
                                color="currentColor"
                                class="mr-1"
                              ></v2-icon
                              >{{ $t("settings.templates.actions.upload") }}</v2-button
                            >
                            <v2-button danger sm @click.native="deleteFile(setting.id)"
                              ><v2-icon
                                :data="require('@icon/Icon=trash.svg')"
                                width="auto"
                                height="14px"
                                color="currentColor"
                                class="mr-1"
                              ></v2-icon
                              >{{ $t("settings.templates.actions.delete") }}</v2-button
                            >
                          </div>
                          <p
                            class="paragraph-3 text-v2-neutral-500 mt-1"
                            v-html="$h.getLanguageValue(setting.description)"
                          ></p>
                        </div>
                      </div>
                    </v2-col>
                    <v2-col
                      col6
                      noSpaceX
                      noSpace
                      v-if="
                        setting.type !== 'label' &&
                        setting.type !== 'textarea' &&
                        setting.type !== 'media' &&
                        setting.type !== 'radio' &&
                        $h.getLanguageValue(setting.description) !== ''
                      "
                    >
                      <!-- eslint-disable max-len -->
                      <div
                        class="paragraph-3 text-v2-neutral-500 flex items-center h-full md:pl-4 md:ml-4 md:border-l md:border-v2-neutral-300"
                      >
                        <!-- eslint-enable max-len -->
                        <div v-html="$h.getLanguageValue(setting.description)"></div>
                      </div>
                    </v2-col>
                  </v2-row>
                </div>
              </div>
              <div v-if="currentSettings[index].with_subcategories">
                <div
                  v-for="(subcategory, subCatKey, subCatIndex) in currentSettingCategory.category_settings"
                  class="w-full"
                  :key="subCatKey"
                >
                  <label class="mb-0 paragraph-2 text-v2-neutral-800 block">
                    {{ $h.getLanguageValue(subcategory.title) }}
                  </label>
                  <p
                    class="paragraph-3 text-v2-neutral-500 flex items-center h-full mb-8"
                    v-html="$h.getLanguageValue(subcategory.description)"
                  ></p>
                  <div class="w-full mb-6" v-for="(setting, index) in subcategory.settings" :key="index">
                    <v2-row>
                      <v2-col col6 noSpace noSpaceX v-if="setting.type === 'text'">
                        <v2-textfield
                          :iconSuffix="require('@icon/Icon=globe.svg')"
                          :label="$h.getLanguageValue(setting.title)"
                          @input="setUnsavedChanges({ val: true })"
                          v-if="setting.translatable"
                          v-model="settingValues[currentTemplateLocaleOption][setting.id]"
                          :placeholder="$h.getLanguageValue(setting.title)"
                        />
                        <v2-textfield
                          :label="$h.getLanguageValue(setting.title)"
                          @input="setUnsavedChanges({ val: true })"
                          v-if="!setting.translatable"
                          v-model="settingValues[defaultLocale][setting.id]"
                          :placeholder="$h.getLanguageValue(setting.title)"
                        />
                      </v2-col>
                      <v2-col col6 noSpace noSpaceX v-if="setting.type === 'url'">
                        <v2-textfield
                          :iconSuffix="require('@icon/Icon=globe.svg')"
                          :label="$h.getLanguageValue(setting.title)"
                          @input="setUnsavedChanges({ val: true })"
                          v-if="setting.translatable"
                          v-model="settingValues[currentTemplateLocaleOption][setting.id]"
                          :placeholder="$h.getLanguageValue(setting.title)"
                        />
                        <v2-textfield
                          :label="$h.getLanguageValue(setting.title)"
                          @input="setUnsavedChanges({ val: true })"
                          v-if="!setting.translatable"
                          v-model="settingValues[defaultLocale][setting.id]"
                          :placeholder="$h.getLanguageValue(setting.title)"
                        />
                      </v2-col>
                      <v2-col col6 noSpace noSpaceX v-if="setting.type === 'color'">
                        <v2-color-picker
                          :label="$h.getLanguageValue(setting.title)"
                          @update="updateColorValue"
                          :uid="setting.id"
                          :value="
                            colorValues.hasOwnProperty(setting.id) ? colorValues[setting.id] : 'rgba(255,255,255,1)'
                          "
                        ></v2-color-picker>
                      </v2-col>
                      <v2-col col6 noSpace noSpaceX v-if="setting.type === 'checkbox'">
                        <label class="mb-0 paragraph-2 text-v2-neutral-800 block">
                          {{ $h.getLanguageValue(setting.title) }}
                        </label>
                        <v-switch
                          @input="setUnsavedChanges({ val: true })"
                          name="switch"
                          :labels="{ true: '', false: '' }"
                          v-model="settingValues[defaultLocale][setting.id]"
                        ></v-switch>
                      </v2-col>
                      <v2-col col6 noSpace noSpaceX v-if="setting.type.indexOf('dropdown_') === 0">
                        <v2-select
                          v-if="dropdownValues.hasOwnProperty(setting.id)"
                          :label="$h.getLanguageValue(setting.title)"
                          :placeholder="''"
                          :customLabel="getTitle"
                          :multiple="false"
                          :value="dropdownValues[setting.id]"
                          :options="setting.options"
                          :id="'setting_id_' + setting.id"
                          :name="'setting_name_' + setting.id"
                          @select="
                            (option) => {
                              selectOption(option, setting.id);
                            }
                          "
                          :allowEmpty="false"
                          :searchable="false"
                          :closeOnSelect="true"
                          :clearOnSelect="false"
                          openDir="bottom"
                        >
                        </v2-select>
                      </v2-col>
                      <v2-col col12 noSpaceX v-if="setting.type === 'textarea'">
                        <label class="mb-0 paragraph-2 text-v2-neutral-800 block">{{
                          $h.getLanguageValue(setting.title)
                        }}</label>
                        <p
                          class="paragraph-3 text-v2-neutral-500 flex items-center h-full"
                          v-html="$h.getLanguageValue(setting.description)"
                        ></p>
                        <v2-textarea
                          tabIndent
                          rows="20"
                          @input="setUnsavedChanges({ val: true })"
                          v-model="settingValues[defaultLocale][setting.id]"
                          :class="{
                            textAreaPreformatted: setting.preformatted,
                          }"
                        ></v2-textarea>
                      </v2-col>
                      <v2-col col12 noSpaceX v-if="setting.type === 'radio'">
                        <label class="mb-0 paragraph-2 text-v2-neutral-800 block">{{
                          $h.getLanguageValue(setting.title)
                        }}</label>
                        <p
                          class="paragraph-3 text-v2-neutral-500 flex items-center h-full"
                          v-html="$h.getLanguageValue(setting.description)"
                        ></p>
                        <v2-radio-group
                          :name="setting.id"
                          :value="settingValues[defaultLocale][setting.id]"
                          v-model="settingValues[defaultLocale][setting.id]"
                          :options="formatRadioOptions(setting.options)"
                        ></v2-radio-group>
                      </v2-col>
                      <v2-col col12 noSpaceX v-if="setting.type === 'media'">
                        <label class="mb-0 paragraph-2 text-v2-neutral-800 block">{{
                          $h.getLanguageValue(setting.title)
                        }}</label>
                        <div class="flex flex-wrap">
                          <div
                            class="flex flex-shrink-0 relative mr-4 items-center border-v2-neutral-200 mb-4"
                            :class="{ border: !mediaValues[setting.id] || !mediaValues[setting.id].imageUrl }"
                            :style="{
                              width: setSettingWidth(setting),
                              height: setSettingHeight(setting),
                            }"
                          >
                            <v2-lazy-load
                              class="border border-v2-neutral-200"
                              v-if="mediaValues[setting.id]"
                              :isLoading="
                                mediaValues[setting.id] &&
                                mediaValues[setting.id].uid &&
                                !mediaValues[setting.id].imageUrl
                              "
                              :style="{
                                width: setSettingWidth(setting),
                                height: setSettingHeight(setting),
                              }"
                              :image="mediaValues[setting.id].imageUrl"
                            ></v2-lazy-load>
                          </div>
                          <div>
                            <div>
                              <v2-button v2Secondary sm @click.native="openUppyModal(setting.id)" class="mr-2"
                                ><v2-icon
                                  :data="require('@icon/Icon=cloud.svg')"
                                  fill
                                  width="auto"
                                  height="14px"
                                  color="currentColor"
                                  class="mr-1"
                                ></v2-icon
                                >{{ $t("settings.templates.actions.upload") }}</v2-button
                              >
                              <v2-button danger sm @click.native="deleteFile(setting.id)"
                                ><v2-icon
                                  :data="require('@icon/Icon=trash.svg')"
                                  width="auto"
                                  height="14px"
                                  color="currentColor"
                                  class="mr-1"
                                ></v2-icon
                                >{{ $t("settings.templates.actions.delete") }}</v2-button
                              >
                            </div>
                            <p
                              class="paragraph-3 text-v2-neutral-500 mt-1"
                              v-html="$h.getLanguageValue(setting.description)"
                            ></p>
                          </div>
                        </div>
                      </v2-col>
                      <v2-col
                        col6
                        noSpaceX
                        noSpace
                        v-if="setting.type !== 'textarea' && $h.getLanguageValue(setting.description) !== ''"
                      >
                        <!-- eslint-disable max-len -->
                        <div
                          class="paragraph-3 text-v2-neutral-500 flex items-center h-full md:pl-4 md:ml-4 md:border-l md:border-v2-neutral-300"
                        >
                          <!-- eslint-enable max-len -->
                          <div v-html="$h.getLanguageValue(setting.description)"></div>
                        </div>
                      </v2-col>
                    </v2-row>
                  </div>
                  <hr
                    class="my-8"
                    :class="{
                      hidden: Object.keys(currentSettingCategory.category_settings).length - 1 === subCatIndex,
                    }"
                  />
                </div>
              </div>
            </div>
            <v-spinner :loading="!templateIsReady" class="mt-100px" style="z-index: 9999"></v-spinner>
          </div>
        </v-tabs>
      </v2-col>
    </v2-row>
  </v2-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import listSettings from "@/mixins/listSettings.js";
import templateSnippets from "@/mixins/dropdowns/templateSnippets.js";
import teamAbilities from "@/mixins/teamAbilities.js";

export default {
  name: "Template",
  mixins: [listSettings, templateSnippets, teamAbilities],
  data() {
    return {
      moreDropdown: [],
      breadcrumbTemplateItems: [
        {
          label: this.$t("menus.settings"),
          route: { name: "Settings", params: { tab: "account" } },
        },
        {
          label: this.$t("menus.templates"),
          route: { name: "Settings", params: { tab: "templates" } },
        },
      ],
      breadcrumbMobileItem: {
        label: this.$t("menus.templates"),
        route: { name: "Settings", params: { tab: "templates" } },
      },
      tabs: [{ label: this.$t("menus.general"), slug: "_general" }, this.$t("menus.textTemplates")],
      currentSettingUid: null,
      activeBoxes: [],
      name: "",
      locale: "",
      localeOptions: [],
      languages: [],
      languageOptions: [],
      currentDeleteSnippetUid: "",
      item: {},
      amountTry: 10,
      timeWaitStart: 3000,
      timer: null,
      tempAmountTry: 10,
      tempTimeWaitStart: 3000,
      tempTimer: null,
      defaultLocale: null,
      templateLocaleOption: null,
      templateLocaleOptions: [],
      settingValues: {},
      colorValues: {},
      dropdownValues: {},
      mediaValues: {},
      mediaParams: {
        rendermethod: "fit",
        width: "2000",
        height: "1500",
      },
      imagesLoaded: [],
      uppyModalOpened: false,
      uploadConfig: {},
      cropperConfig: {},
    };
  },
  created() {
    this.loadPageData(this.$route.params.uid);
  },
  destroyed() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    if (this.tempTimer) {
      clearTimeout(this.tempTimer);
    }
  },
  methods: {
    ...mapActions([
      "getTemplate",
      "getTeamLanguages",
      "editTemplate",
      "getTemplateSnippets",
      "deleteTemplateSnippet",
      "setDefaultTemplateSnippet",
      "templateApiRequest",
      "setUnsavedChanges",
      "getTemplateLibraryFolders",
      "getTemplateLibraryFile",
      "getTemplateFilePreview",
      "getTeamTemplateBuilderSession",
    ]),
    loadPageData(uid) {
      const vm = this;
      vm.$store.commit("GLOBAL_CUSTOM_LOADING_STARTED");
      vm.getTemplate({ template: uid, customLoading: true })
        .then(() => {
          vm.setTabs();
          vm.reloadTemplateUntilReady(false);
          vm.breadcrumbTemplateItems.push({
            label: vm.currentTemplate.name,
            active: true,
          });
          if (vm.$route.params.tab === "_general") {
            vm.setFormModels();
            vm.getTeamLanguages()
              .then(() => {
                if (vm.teamLanguages) {
                  vm.teamLanguages.forEach((lang) => {
                    vm.localeOptions.push({
                      label: vm.$t('lang["' + lang + '"]'),
                      value: lang,
                    });
                    vm.languageOptions.push({
                      label: vm.$t('lang["' + lang + '"]'),
                      value: lang,
                    });
                  });
                }
                vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
              })
              .catch(() => {
                vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
              });
          } else if (vm.$route.params.tab === "templateSnippets") {
            vm.moreDropdown = vm.setSnippetDropdown;
            const p = {
              search: vm.teamsSettings[vm.$route.params.team].listsSettings.templateSnippets.search,
              page: vm.teamsSettings[vm.$route.params.team].listsSettings.templateSnippets.pagination.page,
              per_page: vm.teamsSettings[vm.$route.params.team].listsSettings.templateSnippets.pagination.perPage,
              order_col: vm.teamsSettings[vm.$route.params.team].listsSettings.templateSnippets.order.col,
              order_dir: vm.teamsSettings[vm.$route.params.team].listsSettings.templateSnippets.order.dir,
            };
            const params = vm.setFilterRequestObjectFromStore("templateSnippets", p);
            vm.getTemplateSnippets({
              template: uid,
              setListsSettings: true,
              params,
            })
              .then(() => {
                vm.initSort("templateSnippets");
                vm.initShowAs("templateSnippets");
                vm.initFilters("templateSnippets");
                vm.setDropdownOptions();
                vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
              })
              .catch((err) => {
                console.log(">>>>>>>>>>>>>>>", err);
              });
          } else {
            vm.defaultLocale = this.currentTemplate.locale;
            vm.setTemplateLocaleOptions();
            vm.initSettingValues();
            vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
          }
        })
        .catch(() => {
          vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
        });
    },
    setTabs() {
      this.currentTemplate.theme.template_defaults.settings.template.forEach((cat) => {
        this.tabs.push(this.$h.getLanguageValue(cat.title));
      });
    },
    reloadTemplateUntilReady(setValues = true) {
      if (!this.isReadyCalculate()) {
        const vm = this;
        vm.tempTimer = setTimeout(function () {
          vm.getTemplate({
            template: vm.$route.params.uid,
            customLoading: true,
          })
            .then(() => {
              vm.tempAmountTry = vm.tempAmountTry - 1;
              if (vm.tempAmountTry <= 0) {
                vm.tempAmountTry = 10;
                vm.tempTimeWaitStart = vm.tempTimeWaitStart + 60000;
              }
              vm.reloadTemplateUntilReady();
            })
            .catch();
        }, vm.tempTimeWaitStart);
      } else {
        if (setValues) {
          this.initSettingValues();
        }
      }
    },
    submitTemplate() {
      this.errors.clear();
      this.$validator.validateAll().then((result) => {
        let localeMissing = true;
        if (this.languages.some((lang) => lang.value === this.locale.value)) {
          localeMissing = false;
        } else {
          this.errors.add({
            field: "languages",
            msg: this.$t("template.form.validation.localeMissingInAvailableLanguages"),
          });
        }
        if (result && !localeMissing) {
          const langs = [];
          this.languages.forEach((lang) => {
            langs.push(lang.value);
          });
          const data = {
            name: this.name,
            locale: this.locale.value,
            languages: langs,
          };
          this.editTemplate({
            params: { template: this.$route.params.uid },
            data,
          })
            .then(() => {
              this.$snotify.success(this.$t("toasts.editedTemplate.success"), this.$t("toasts.success"), {
                icon: this.$successIcon,
              });
            })
            .catch((err) => {
              if (err.response && !err.response.data.error.custom) {
                this.$snotify.error(this.$t("toasts.editedTemplate.error"), this.$t("toasts.error"), {
                  timeout: 0,
                  icon: this.$errorIcon,
                });
              }
            });
        } else {
          this.$snotify.error(this.$t("toasts.createdTemplate.validation"), this.$t("toasts.error"), {
            timeout: 0,
            icon: this.$errorIcon,
          });
        }
      });
    },
    setSnippetDropdown(snippet) {
      return new Promise((resolve) => {
        if (!this.$h.templateSnippetsStatus(snippet.status, "default", false)) {
          resolve([
            {
              options: [
                {
                  label: this.$t("actions.standard"),
                  method: (snippet) => {
                    this.setStandardSnippet(snippet.uid);
                  },
                  doNotShowActiveState: true,
                },
                {
                  label: this.$t("actions.delete"),
                  method: (snippet) => {
                    this.showDeleteTemplateSnippet(snippet.uid);
                  },
                  color: "text-danger",
                  doNotShowActiveState: true,
                },
              ],
            },
          ]);
        } else {
          resolve([]);
        }
      });
    },
    setStandardSnippet(uid) {
      this.setDefaultTemplateSnippet({
        params: {
          template: this.currentTemplate.uid,
          snippet: uid,
        },
      }).then(() => {
        this.getSnippets({
          search: this.searchQuery,
          page: this.teamsSettings[this.$route.params.team].listsSettings.templateSnippets.page,
          per_page: this.teamsSettings[this.$route.params.team].listsSettings.templateSnippets.perPage,
          order_col: this.teamsSettings[this.$route.params.team].listsSettings.templateSnippets.order.col,
          order_dir: this.teamsSettings[this.$route.params.team].listsSettings.templateSnippets.order.dir,
        })
          .then(() => {
            this.$snotify.success(this.$t("toasts.defaultTemplateSnippet.success"), this.$t("toasts.success"), {
              icon: this.$successIcon,
            });
          })
          .catch((err) => {
            if (err.response && !err.response.data.error.custom) {
              this.$snotify.error(this.$t("toasts.defaultTemplateSnippet.error"), this.$t("toasts.error"), {
                timeout: 0,
                icon: this.$errorIcon,
              });
            }
          });
      });
    },
    showDeleteTemplateSnippet(uid) {
      this.currentDeleteSnippetUid = uid;
      this.$modal.show("delete-snippets-modal");
    },
    deleteModalHandler(uid) {
      this.currentDeleteSnippetUid = null;
      this.$modal.hide("delete-snippets-modal");
      this.deleteTemplateSnippet({
        params: { template: this.currentTemplate.uid, snippet: uid },
      })
        .then(() => {
          this.getSnippets({
            search: this.searchQuery,
            page: this.teamsSettings[this.$route.params.team].listsSettings.templateSnippets.page,
            per_page: this.teamsSettings[this.$route.params.team].listsSettings.templateSnippets.perPage,
            order_col: this.teamsSettings[this.$route.params.team].listsSettings.templateSnippets.order.col,
            order_dir: this.teamsSettings[this.$route.params.team].listsSettings.templateSnippets.order.dir,
          }).then(() => {
            this.$snotify.success(this.$t("toasts.deleteTemplateSnippet.success"), this.$t("toasts.success"), {
              icon: this.$successIcon,
            });
          });
        })
        .catch((err) => {
          if (err.response && !err.response.data.error.custom) {
            this.$snotify.error(this.$t("toasts.deleteTemplateSnippet.error"), this.$t("toasts.error"), {
              timeout: 0,
              icon: this.$errorIcon,
            });
          }
        });
    },
    sortTemplateSnippets(col, dir) {
      const params = {
        search: this.searchQuery,
        page: this.teamsSettings[this.$route.params.team].listsSettings.templateSnippets.pagination.page,
        per_page: this.teamsSettings[this.$route.params.team].listsSettings.templateSnippets.pagination.perPage,
        order_col: col,
        order_dir: dir,
      };
      this.getSnippets(params);
    },
    changeTemplateSnippetsPage(page, perPage) {
      const params = {
        search: this.searchQuery,
        page,
        per_page: perPage,
        order_col: this.teamsSettings[this.$route.params.team].listsSettings.templateSnippets.order.col,
        order_dir: this.teamsSettings[this.$route.params.team].listsSettings.templateSnippets.order.dir,
      };
      this.getSnippets(params);
    },
    getSnippets(params) {
      this.filterDropdown[0].options.forEach((option) => {
        if (option.active && option.switch && option.switch.name && !option.select) {
          params[option.switch.name] = option.switch.model;
        }
      });
      return this.getTemplateSnippets({
        template: this.currentTemplate.uid,
        params,
      });
    },
    setTemplateSnippetsFilters() {
      const params = {
        search: this.searchQuery,
        page: 1,
        per_page: this.teamsSettings[this.$route.params.team].listsSettings.templateSnippets.pagination.perPage,
        order_col: this.teamsSettings[this.$route.params.team].listsSettings.templateSnippets.order.col,
        order_dir: this.teamsSettings[this.$route.params.team].listsSettings.templateSnippets.order.dir,
      };
      this.getSnippets(params);
      this.$eventbus.$emit("closeAllDropdowns");
    },
    setFormModels() {
      this.name = this.currentTemplate.name;
      this.locale = {
        label: this.$t('lang["' + this.currentTemplate.locale + '"]'),
        value: this.currentTemplate.locale,
      };
      this.currentTemplate.languages.forEach((lang) => {
        this.languages.push({
          label: this.$t('lang["' + lang + '"]'),
          value: lang,
        });
      });
    },
    setActiveTab(slug) {
      switch (slug) {
        case "_general":
          return this.$goTo("Template", {
            uid: this.currentTemplate.uid,
            tab: "_general",
          });
        case this.$t("menus.textTemplates").replace(/\s+/g, "-").toLowerCase():
          return this.$goTo("Template", {
            uid: this.currentTemplate.uid,
            tab: "templateSnippets",
          });
        default:
          return this.$goTo("Template", {
            uid: this.currentTemplate.uid,
            tab: slug.replace(/\s+/g, "-").toLowerCase(),
          });
      }
    },
    getActiveTab() {
      switch (this.$route.params.tab) {
        case "_general":
          return "_general";
        case "templateSnippets":
          return this.$t("menus.textTemplates").replace(/\s+/g, "-").toLowerCase();
        default:
          return this.$route.params.tab;
      }
    },
    preselectAvailableLanguage(locale) {
      if (this.languages.filter((e) => e.value === locale.value).length <= 0) {
        this.languages.push(locale);
      }
    },
    isReadyCalculate() {
      if (this.currentTemplate && this.currentTemplate.current_draft && this.currentTemplate.current_draft.statusBits) {
        return this.$h.globalIsBitSet(
          this.currentTemplate.current_draft.status,
          this.currentTemplate.current_draft.statusBits.READY.bit
        );
      } else {
        return false;
      }
    },
    isCreatingCalculate() {
      if (this.currentTemplate && this.currentTemplate.current_draft && this.currentTemplate.current_draft.statusBits) {
        return this.$h.globalIsBitSet(
          this.currentTemplate.current_draft.status,
          this.currentTemplate.current_draft.statusBits.CREATING.bit
        );
      } else {
        return false;
      }
    },
    isPublishingCalculate() {
      if (this.currentTemplate && this.currentTemplate.current_draft && this.currentTemplate.current_draft.statusBits) {
        return this.$h.globalIsBitSet(
          this.currentTemplate.current_draft.status,
          this.currentTemplate.current_draft.statusBits.PUBLISHING.bit
        );
      } else {
        return false;
      }
    },
    isModifiedCalculate() {
      if (this.currentTemplate && this.currentTemplate.current_draft && this.currentTemplate.current_draft.statusBits) {
        return this.$h.globalIsBitSet(
          this.currentTemplate.current_draft.status,
          this.currentTemplate.current_draft.statusBits.MODIFIED.bit
        );
      } else {
        return false;
      }
    },
    isPublishedCalculate() {
      if (this.currentTemplate && this.currentTemplate.current_draft && this.currentTemplate.current_draft.statusBits) {
        return this.$h.globalIsBitSet(
          this.currentTemplate.current_draft.status,
          this.currentTemplate.current_draft.statusBits.PUBLISHED.bit
        );
      } else {
        return false;
      }
    },
    isResettingCalculate() {
      if (this.currentTemplate && this.currentTemplate.current_draft && this.currentTemplate.current_draft.statusBits) {
        return this.$h.globalIsBitSet(
          this.currentTemplate.current_draft.status,
          this.currentTemplate.current_draft.statusBits.RESETTING.bit
        );
      } else {
        return false;
      }
    },
    isSeedingCalculate() {
      if (this.currentTemplate && this.currentTemplateMeta) {
        return this.$h.globalIsBitSet(this.currentTemplate.status, this.currentTemplateMeta.SEEDING.bit);
      } else {
        return false;
      }
    },
    initSettingValues() {
      this.setUnsavedChanges({ val: false });
      this.settingValues = {};
      this.colorValues = {};
      this.mediaValues = {};
      this.settingValues[this.defaultLocale] = {};
      this.settingValues[this.currentTemplateLocaleOption] = {};
      if (this.currentSettingCategory) {
        if (this.currentSettingCategory.with_subcategories) {
          let subcategories = this.currentSettingCategory.category_settings;
          Object.entries(subcategories).forEach(([key]) => {
            let settings = subcategories[key].settings;
            for (let v in settings) {
              if (Object.prototype.hasOwnProperty.call(this.templateSettings, settings[v].id)) {
                if (settings[v].type.indexOf("dropdown_") === 0) {
                  let optionFound = false;
                  for (let o in settings[v].options) {
                    if (
                      settings[v].type === "dropdown_font" &&
                      settings[v].options[o].value === this.templateSettings[settings[v].id].value &&
                      settings[v].options[o].import === this.templateSettings[settings[v].id].import
                    ) {
                      optionFound = true;
                    } else if (settings[v].options[o].value === this.templateSettings[settings[v].id]) {
                      optionFound = true;
                    }
                    if (optionFound) {
                      this.settingValues[this.defaultLocale][settings[v].id] = settings[v].options[o];
                      break;
                    }
                  }
                  if (!optionFound) {
                    this.settingValues[this.defaultLocale][settings[v].id] = this.getDropdownDefaultSetting(
                      settings[v]
                    );
                  }
                  this.$set(
                    this.dropdownValues,
                    settings[v].id,
                    this.settingValues[this.defaultLocale][settings[v].id]
                  );
                } else {
                  if (settings[v].translatable) {
                    this.settingValues[this.currentTemplateLocaleOption][settings[v].id] = this.$h.getLanguageValue(
                      this.templateSettings[settings[v].id],
                      this.currentTemplateLocaleOption
                    );
                  } else {
                    this.settingValues[this.defaultLocale][settings[v].id] = this.templateSettings[settings[v].id];
                    if (settings[v].type === "color") {
                      this.colorValues[settings[v].id] = this.templateSettings[settings[v].id];
                    }
                    if (settings[v].type === "media") {
                      const settingUid = settings[v].id;
                      const vm = this;
                      const payload = {
                        customLoading: true,
                        params: {
                          template: this.currentTemplate.uid,
                        },
                      };
                      vm.getTemplateLibraryFolders(payload)
                        .then(() => {
                          vm.$set(
                            vm.mediaValues,
                            settingUid,
                            JSON.parse(JSON.stringify(vm.templateSettings[settingUid]))
                          );
                          vm.$set(vm.mediaValues[settingUid], "imageUrl", null);
                          vm.untilReady(settingUid, vm.mediaValues[settingUid]);
                        })
                        .catch();
                    }
                  }
                }
              } else {
                this.setOptionDefaultInitValue(settings[v]);
              }
            }
          });
        } else {
          let settings = this.currentSettingCategory.category_settings;
          for (let v in settings) {
            if (Object.prototype.hasOwnProperty.call(this.templateSettings, settings[v].id)) {
              if (settings[v].type.indexOf("dropdown_") === 0) {
                let optionFound = false;
                for (let o in settings[v].options) {
                  if (
                    settings[v].type === "dropdown_font" &&
                    settings[v].options[o].value === this.templateSettings[settings[v].id].value &&
                    settings[v].options[o].import === this.templateSettings[settings[v].id].import
                  ) {
                    optionFound = true;
                  } else if (settings[v].options[o].value === this.templateSettings[settings[v].id]) {
                    optionFound = true;
                  }
                  if (optionFound) {
                    this.settingValues[this.defaultLocale][settings[v].id] = settings[v].options[o];
                    break;
                  }
                }
                if (!optionFound) {
                  this.settingValues[this.defaultLocale][settings[v].id] = this.getDropdownDefaultSetting(settings[v]);
                }
                this.$set(this.dropdownValues, settings[v].id, this.settingValues[this.defaultLocale][settings[v].id]);
              } else {
                if (settings[v].translatable) {
                  this.settingValues[this.currentTemplateLocaleOption][settings[v].id] = this.$h.getLanguageValue(
                    this.templateSettings[settings[v].id],
                    this.currentTemplateLocaleOption
                  );
                } else {
                  this.settingValues[this.defaultLocale][settings[v].id] = this.templateSettings[settings[v].id];
                  if (settings[v].type === "color") {
                    this.colorValues[settings[v].id] = this.templateSettings[settings[v].id];
                  }
                  if (settings[v].type === "media") {
                    const settingUid = settings[v].id;
                    const vm = this;
                    const payload = {
                      customLoading: true,
                      params: {
                        template: this.currentTemplate.uid,
                      },
                    };
                    vm.getTemplateLibraryFolders(payload)
                      .then(() => {
                        vm.$set(
                          vm.mediaValues,
                          settingUid,
                          JSON.parse(JSON.stringify(vm.templateSettings[settingUid]))
                        );
                        if (vm.mediaValues[settingUid]) vm.$set(vm.mediaValues[settingUid], "imageUrl", null);
                        vm.untilReady(settingUid, vm.mediaValues[settingUid]);
                      })
                      .catch();
                    // if (this.mediaValues[settings[v].id]) this.mediaValues[settings[v].id].imageLoaded = false;
                  }
                }
              }
            } else {
              this.setOptionDefaultInitValue(settings[v]);
            }
          }
        }
      }
      this.localeChanged = false;
    },
    setOptionDefaultInitValue(setting) {
      if (Object.prototype.hasOwnProperty.call(setting, "initial")) {
        if (setting.type.indexOf("dropdown_") === 0) {
          this.settingValues[this.defaultLocale][setting.id] = this.getDropdownDefaultSetting(setting);
          this.$set(this.dropdownValues, setting.id, this.settingValues[this.defaultLocale][setting.id]);
        } else {
          if (setting.translatable) {
            this.$set(
              this.settingValues[this.currentTemplateLocaleOption],
              setting.id,
              this.$h.getLanguageValue(setting.initial, this.currentTemplateLocaleOption)
            );
          } else {
            this.$set(this.settingValues[this.defaultLocale], setting.id, setting.initial);
            if (setting.type === "color") {
              this.colorValues[setting.id] = setting.initial;
            }
          }
        }
      } else if (setting.type === "color") {
        this.settingValues[this.defaultLocale][setting.id] = "rgba(255,255,255,1)";
        this.colorValues[setting.id] = "rgba(255,255,255,1)";
      } else if (Object.prototype.hasOwnProperty.call(this.templateSettings, setting.id)) {
        if (setting.type.indexOf("dropdown_")) {
          this.$delete(this.settingValues[this.defaultLocale], setting.id);
          this.$delete(this.dropdownValues, setting.id);
        } else {
          this.$set(this.settingValues[this.defaultLocale], setting.id, "");
        }
      }
    },
    getDropdownDefaultSetting(setting) {
      let optionFound = false;
      for (let o in setting.options) {
        if (
          setting.type === "dropdown_font" &&
          setting.options[o].value === setting.initial.value &&
          setting.options[o].import === setting.initial.import
        ) {
          optionFound = true;
        } else if (setting.options[o].value === setting.initial) {
          optionFound = true;
        }
        if (optionFound) {
          return setting.options[o];
        }
      }
      return { name: "", value: "" };
    },
    selectOption(option, id) {
      this.setUnsavedChanges({ val: true });
      this.removeOption(option, id);
      let key = id.replace("setting_id_", "");
      this.$nextTick(() => {
        this.$set(this.settingValues[this.defaultLocale], key, option);
        this.$set(this.dropdownValues, key, this.settingValues[this.defaultLocale][key]);
      });
    },
    removeOption(option, id) {
      this.setUnsavedChanges({ val: true });
      let key = id.replace("setting_id_", "");
      if (Object.prototype.hasOwnProperty.call(this.settingValues[this.defaultLocale], key)) {
        this.$delete(this.settingValues[this.defaultLocale], key);
        this.$delete(this.dropdownValues, key);
        this.$nextTick(() => {
          let settings = this.currentSettingCategory.category_settings;
          for (let v in settings) {
            if (settings[v].id === key) {
              this.$set(this.settingValues[this.defaultLocale], key, this.getDropdownDefaultSetting(settings[v]));
              break;
            }
          }
          this.$set(this.dropdownValues, key, this.settingValues[this.defaultLocale][key]);
        });
      }
    },
    getTitle(option) {
      return this.$h.getLanguageValue(option.title);
    },
    updateColorValue(val) {
      this.setUnsavedChanges({ val: true });
      this.$delete(this.colorValues, val.uid);
      this.$nextTick(() => {
        this.$set(this.colorValues, val.uid, val.value);
        this.$set(this.settingValues[this.defaultLocale], val.uid, val.value);
      });
    },
    toggleBox(index) {
      if (this.activeBoxes.includes(index)) {
        const i = this.activeBoxes.indexOf(index);
        if (i > -1) {
          this.activeBoxes.splice(i, 1);
        }
      } else {
        this.activeBoxes.push(index);
      }
    },
    toggleUppyModal(val, config = false) {
      this.$set(this.uploadConfig, "max_size", this.teamUploadConfig().max_size);
      this.$set(this.uploadConfig, "extensions", { white_list: [".jpg", ".jpeg", ".gif", ".png", ".svg", ".bmp"] });

      if (config) {
        if (config.cropperConfig) {
          this.$set(this, "cropperConfig", config.cropperConfig);
        }
        if (config.params && config.params.width && config.params.height) {
          this.$set(this.cropperConfig, "options", {
            aspectRatio: config.params.width / config.params.height,
          });
        }
        if (config.max_size) {
          this.$set(this.uploadConfig, "max_size", this.teamUploadConfig(config.max_size).max_size);
        }
        if (config.allowedFileTypes) {
          this.$set(this.uploadConfig, "extensions", { white_list: config.allowedFileTypes });
        }
      }
      this.uppyModalOpened = val;
      if (!val) {
        this.$eventbus.$emit("resetUppy");
      }
    },
    openUppyModal(setting) {
      this.currentSettingUid = setting.id;
      let config = {};
      if (setting.params) {
        config.params = setting.params;
      }
      if (setting.uploadConfig) {
        config.uploadConfig = setting.uploadConfig;
      }
      if (setting.cropperConfig) {
        config.cropperConfig = setting.cropperConfig;
      }
      this.toggleUppyModal(true, config);
    },
    deleteFile(settingUid) {
      this.mediaValues[settingUid] = null;
      this.setUnsavedChanges({ val: true });
    },
    createFile(data) {
      let vm = this;

      return new Promise((resolve, reject) => {
        const payload = {
          customLoading: true,
          params: {
            template: this.currentTemplate.uid,
          },
        };

        vm.getTemplateLibraryFolders(payload)
          .then(() => {
            const payload = {
              apiMethod: "createFile",
              customLoading: true,
              params: {
                template: this.currentTemplate.uid,
                tfolder: this.templateLibraryFolders[0].uid,
              },
              data: data.data,
            };

            vm.templateApiRequest(payload)
              .then((res) => {
                resolve(res);
              })
              .catch((err) => {
                reject(err);
                console.log("error: ", err);
              });
          })
          .catch((err) => {
            reject(err);
            console.log("error: ", err);
          });
      });
    },
    getLanguageValues(setting, locale) {
      let found = false;
      const arrayKey = locale === this.defaultLocale ? "default" : locale;
      let value = "";
      if (
        Object.prototype.hasOwnProperty.call(this.settingValues, locale) &&
        Object.prototype.hasOwnProperty.call(this.settingValues[locale], setting.id)
      ) {
        found = true;
        value = this.settingValues[locale][setting.id];
      } else if (
        Object.prototype.hasOwnProperty.call(this.templateSettings, setting.id) &&
        Object.prototype.hasOwnProperty.call(this.templateSettings[setting.id], arrayKey)
      ) {
        found = true;
        value = this.templateSettings[setting.id][arrayKey];
      } else if (Object.prototype.hasOwnProperty.call(setting.initial, arrayKey)) {
        found = true;
        value = setting.initial[arrayKey];
      }
      return {
        found: found,
        value: value,
      };
    },
    save() {
      let data = {};
      if (this.currentSettingCategory.with_subcategories) {
        Object.entries(this.currentSettingCategory.category_settings).forEach(([key]) => {
          // var settings = subcategories[key].settings
          for (const v in this.currentSettingCategory.category_settings[key].settings) {
            const setting = this.currentSettingCategory.category_settings[key].settings[v];
            switch (setting.type) {
              case "color": {
                if (Object.prototype.hasOwnProperty.call(this.settingValues[this.defaultLocale], setting.id)) {
                  if (typeof this.settingValues[this.defaultLocale][setting.id] === "object") {
                    data[setting.id] = this.settingValues[this.defaultLocale][setting.id].hex8;
                  } else {
                    data[setting.id] = this.settingValues[this.defaultLocale][setting.id];
                  }
                }
                break;
              }
              case "text":
              case "url": {
                if (setting.translatable) {
                  const langValues = {
                    default: this.getLanguageValues(setting, this.defaultLocale).value,
                  };
                  for (const l in this.templateLocaleOptions) {
                    if (this.templateLocaleOptions[l].value !== this.defaultLocale) {
                      const val = this.getLanguageValues(setting, this.templateLocaleOptions[l].value);
                      if (val.found) {
                        langValues[this.templateLocaleOptions[l].value] = val.value;
                      }
                    }
                  }
                  data[setting.id] = langValues;
                } else {
                  data[setting.id] = this.settingValues[this.defaultLocale][setting.id];
                }
                break;
              }
              case "dropdown_font": {
                if (Object.prototype.hasOwnProperty.call(this.settingValues[this.defaultLocale], setting.id)) {
                  if (typeof this.settingValues[this.defaultLocale][setting.id] === "object") {
                    data[setting.id] = {
                      value: this.settingValues[this.defaultLocale][setting.id].value,
                      import: this.settingValues[this.defaultLocale][setting.id].import,
                    };
                  } else {
                    data[setting.id] = this.settingValues[this.defaultLocale][setting.id];
                  }
                }
                break;
              }
              case "media": {
                if (Object.prototype.hasOwnProperty.call(this.settingValues[this.defaultLocale], setting.id)) {
                  data[setting.id] = this.mediaValues[setting.id];
                }
                break;
              }
              default: {
                if (Object.prototype.hasOwnProperty.call(this.settingValues[this.defaultLocale], setting.id)) {
                  if (typeof this.settingValues[this.defaultLocale][setting.id] === "object") {
                    data[setting.id] = this.settingValues[this.defaultLocale][setting.id].value;
                  } else {
                    data[setting.id] = this.settingValues[this.defaultLocale][setting.id];
                  }
                }
                break;
              }
            }
          }
        });
      } else {
        for (const v in this.currentSettingCategory.category_settings) {
          const setting = this.currentSettingCategory.category_settings[v];
          switch (setting.type) {
            case "color": {
              if (Object.prototype.hasOwnProperty.call(this.settingValues[this.defaultLocale], setting.id)) {
                if (typeof this.settingValues[this.defaultLocale][setting.id] === "object") {
                  data[setting.id] = this.settingValues[this.defaultLocale][setting.id].hex8;
                } else {
                  data[setting.id] = this.settingValues[this.defaultLocale][setting.id];
                }
              }
              break;
            }
            case "text":
            case "url": {
              if (setting.translatable) {
                const langValues = {
                  default: this.getLanguageValues(setting, this.defaultLocale).value,
                };
                for (const l in this.templateLocaleOptions) {
                  if (this.templateLocaleOptions[l].value !== this.defaultLocale) {
                    const val = this.getLanguageValues(setting, this.templateLocaleOptions[l].value);
                    if (val.found) {
                      langValues[this.templateLocaleOptions[l].value] = val.value;
                    }
                  }
                }
                data[setting.id] = langValues;
              } else {
                data[setting.id] = this.settingValues[this.defaultLocale][setting.id];
              }
              break;
            }
            case "dropdown_font": {
              if (Object.prototype.hasOwnProperty.call(this.settingValues[this.defaultLocale], setting.id)) {
                if (typeof this.settingValues[this.defaultLocale][setting.id] === "object") {
                  data[setting.id] = {
                    value: this.settingValues[this.defaultLocale][setting.id].value,
                    import: this.settingValues[this.defaultLocale][setting.id].import,
                  };
                } else {
                  data[setting.id] = this.settingValues[this.defaultLocale][setting.id];
                }
              }
              break;
            }
            case "media": {
              if (Object.prototype.hasOwnProperty.call(this.settingValues[this.defaultLocale], setting.id)) {
                data[setting.id] = this.mediaValues[setting.id];
              }
              break;
            }
            default: {
              if (Object.prototype.hasOwnProperty.call(this.settingValues[this.defaultLocale], setting.id)) {
                if (typeof this.settingValues[this.defaultLocale][setting.id] === "object") {
                  data[setting.id] = this.settingValues[this.defaultLocale][setting.id].value;
                } else {
                  data[setting.id] = this.settingValues[this.defaultLocale][setting.id];
                }
              }
              break;
            }
          }
        }
      }
      const vm = this;
      vm.$store.commit("GLOBAL_CUSTOM_LOADING_STARTED");
      const payload = {
        apiMethod: "updateTemplateSettings",
        customLoading: true,
        params: {
          template: this.currentTemplate.uid,
          draft: this.currentTemplate.current_draft.uid,
        },
        data: {
          config: data,
          updated_at: this.currentTemplate.current_draft.updated_at,
        },
      };

      vm.templateApiRequest(payload)
        .then(() => {
          vm.setUnsavedChanges({ val: false });
          vm.getTemplate({ template: vm.currentTemplate.uid })
            .then(() => {
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
            })
            .catch(() => {
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
            });
        })
        .catch((err) => {
          vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
          console.log("error: ", err);
        });
    },
    setTemplateLocaleOptions() {
      this.currentTemplate.languages.forEach((lang) => {
        let l = lang;
        let label = "";
        if (lang === this.currentTemplate.locale) {
          label = this.$t("lang." + lang) + " (" + this.$t("lang.default") + ")";
          this.templateLocaleOption = { value: l, label };
        } else {
          label = this.$t("lang." + lang);
        }
        this.templateLocaleOptions.push({ value: l, label });
      });
    },
    completedHandler(file, settingUid) {
      this.setUnsavedChanges({ val: true });
      this.uppyModalOpened = false;
      const data = {
        template: this.currentTemplate.uid,
        file: file.uid,
        folder: this.templateLibraryFolders[0].uid,
        customLoading: true,
      };
      this.getTemplateLibraryFile(data)
        .then((res) => {
          this.setFileSetting({ settingUid, file: res });
        })
        .catch((err) => {
          console.log("ERR", err);
        });
    },
    setFileSetting(data) {
      this.$set(this.mediaValues, data.settingUid, data.file);
      this.$set(this.settingValues[this.defaultLocale], data.settingUid, data.file);
      if (this.mediaValues[data.settingUid]) {
        this.$set(this.mediaValues[data.settingUid], "imageUrl", null);
        this.mediaValues[data.settingUid].settingUrl = null;
        // this.mediaValues[data.settingUid].imageUrl = null;
        this.untilReady(data.settingUid, data.file);
      }
      this.removeVfromA(this.imagesLoaded, data.settingUid);
    },
    removeVfromA(array, value) {
      let index = array.indexOf(value);
      if (index !== -1) {
        array.splice(index, 1);
      }
    },
    untilReady(settingUid, file) {
      if (file) {
        const data = {
          template: this.currentTemplate.uid,
          file: file.uid,
          folder: this.templateLibraryFolders[0].uid,
          customLoading: true,
        };
        const isReady = this.checkIfReady(file);
        if (!isReady) {
          const vm = this;
          vm.timer = setTimeout(function () {
            vm.getTemplateLibraryFile(data)
              .then((f) => {
                vm.mediaValues[settingUid] = f;
                vm.amountTry = vm.amountTry - 1;
                if (vm.amountTry <= 0) {
                  vm.amountTry = 10;
                  vm.timeWaitStart = vm.timeWaitStart + 60000;
                }
                vm.untilReady(settingUid, f);
              })
              .catch();
          }, vm.timeWaitStart);
        } else {
          this.amountTry = 20;
          const thisSetting = this.getSettingById(settingUid);
          let data;
          data = {
            template: this.currentTemplate.uid,
            file: file.uid,
            folder: this.templateLibraryFolders[0].uid,
            params: {
              width: thisSetting.params && thisSetting.params.width ? thisSetting.params.width : this.mediaParams.width,
              height:
                thisSetting.params && thisSetting.params.height ? thisSetting.params.height : this.mediaParams.height,
              rendermethod:
                thisSetting.params && thisSetting.params.rendermethod
                  ? thisSetting.params.rendermethod
                  : this.mediaParams.rendermethod,
            },
            customLoading: true,
          };
          this.getTemplateFilePreview(data)
            .then((res) => {
              this.$set(this.mediaValues[settingUid], "settingUrl", res);
              this.$set(this.mediaValues[settingUid], "imageUrl", res);
            })
            .catch();
        }
      }
    },
    checkIfReady(file) {
      if (this.statiBits && file.status) {
        return (
          this.$h.globalCheckFileStatus(file.status, this.statiBits) !== "VIRUS_SCANNING" &&
          this.$h.globalCheckFileStatus(file.status, this.statiBits) !== "CONVERTING"
        );
      } else {
        return false;
      }
    },
    getSettingById(id) {
      return this.currentSettingCategory.category_settings.find((setting) => {
        if (setting.id === id) return setting;
      });
    },
    globalFormatThemeImage(url, width, height, renderMethod = "fit", page = 1, quality = "1") {
      return url
        .replace("{page}", page)
        .replace("{width}", width)
        .replace("{height}", height)
        .replace("{rendermethod}", renderMethod)
        .replace("{quality}", quality);
    },
    publishTemplateDraft() {
      if (
        !this.templateIsCreating &&
        !this.templateIsPublishing &&
        !this.templateIsResetting &&
        !this.teamsSettings[this.$route.params.team].unsavedChanges
      ) {
        const vm = this;
        const payload = {
          apiMethod: "publishTemplateDraft",
          params: {
            template: this.currentTemplate.uid,
          },
        };

        vm.templateApiRequest(payload)
          .then(() => {
            vm.getTemplate({
              template: vm.$route.params.uid,
              customLoading: true,
            })
              .then(() => {
                if (!this.isReadyCalculate()) {
                  vm.reloadTemplateUntilReady();
                }
              })
              .catch();
            vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
          })
          .catch((err) => {
            vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
            console.log("error: ", err);
          });
      }
    },
    undoTemplateDraftChanges() {
      if (!this.templateIsCreating && !this.templateIsPublishing && !this.templateIsResetting) {
        const vm = this;
        const payload = {
          apiMethod: "undoTemplateDraftChanges",
          params: {
            template: this.currentTemplate.uid,
          },
        };

        vm.templateApiRequest(payload)
          .then(() => {
            vm.getTemplate({
              template: vm.$route.params.uid,
              customLoading: true,
            })
              .then(() => {
                if (!this.isReadyCalculate()) {
                  vm.reloadTemplateUntilReady();
                }
              })
              .catch();
          })
          .catch();
      }
    },
    openPreview() {
      window.open(this.frontendPreviewUrl, "_blank").focus();
    },
    calculateTemplateStatus() {
      if (this.templateIsModified) {
        return "modified";
      }
      if (this.templateIsResetting) {
        return "resetting";
      }
      if (this.templateIsPublishing) {
        return "publishing";
      }
      if (this.templateIsReady) {
        return "published";
      }
      if (this.templateIsCreating || (this.currentTemplate && !this.currentTemplate.current_draft)) {
        return "loading";
      }
      return "error";
    },
    calcImageRatio(setting) {
      if (setting && setting.params && setting.params.width && setting.params.height) {
        return parseInt(setting.params.width) / parseInt(setting.params.height);
      }
    },
    setSettingWidth(setting) {
      if (setting && setting.params && setting.params.width && setting.params.height) {
        if (this.$refs.container && setting.params.width > this.$refs.container.$el.clientWidth) {
          return "100%";
        } else {
          return setting.params.width + "px";
        }
      }
      return "75px";
    },
    setSettingHeight(setting) {
      if (setting && setting.params && setting.params.width && setting.params.height) {
        if (this.$refs.container && setting.params.width > this.$refs.container.$el.clientWidth) {
          return this.$refs.container.$el.clientWidth / this.calcImageRatio(setting) + "px";
        } else {
          return setting.params.height + "px";
        }
      }
      return "75px";
    },
    openTemplateBuilder(template, preview = false) {
      this.$store.commit("GLOBAL_CUSTOM_LOADING_STARTED");
      this.getTeamTemplateBuilderSession({
        params: { template },
        data: { return_url: window.location.href, preview: preview },
      })
        .then(() => {
          if (this.templateBuilderSession.is_locked) {
            this.$snotify.error(
              this.$t("toasts.builder.locked", {
                user: this.$h.formatName(this.templateBuilderSession.lock.user, true),
              }),
              this.$t("toasts.error"),
              {
                timeout: 0,
                icon: this.$errorIcon,
              }
            );
            this.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
          } else if (this.templateBuilderSession.is_seeding) {
            this.$snotify.error(this.$t("toasts.builder.seeding"), this.$t("toasts.error"), {
              timeout: 0,
              icon: this.$errorIcon,
            });
            this.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
          } else {
            location.href = this.templateBuilderSession.builder;
          }
        })
        .catch(() => {
          this.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
        });
    },
    formatRadioOptions(options) {
      let res = [];
      options.forEach((option) => {
        res.push({ value: option.value, label: this.$h.getLanguageValue(option.title) });
      });
      return res;
    },
  },
  computed: {
    templateStatus() {
      return this.calculateTemplateStatus();
    },
    frontendPreviewUrl() {
      return (
        "https://" +
        this.teams[this.$route.params.team].account.team.domain +
        "/t." +
        this.currentTemplate.uid +
        ".d." +
        this.currentTemplate.current_draft.uid
      );
    },
    templateIsReady() {
      return this.isReadyCalculate() || this.isPublishingCalculate();
    },
    templateIsPublishing() {
      return this.isPublishingCalculate();
    },
    templateIsCreating() {
      return this.isCreatingCalculate();
    },
    templateIsModified() {
      return this.isModifiedCalculate();
    },
    templateIsPublished() {
      return this.isPublishedCalculate();
    },
    templateIsResetting() {
      return this.isResettingCalculate();
    },
    currentSettings() {
      return this.currentTemplate.theme.template_defaults.settings.template;
    },
    currentSettingCategory() {
      const i = this.tabs.findIndex(
        (tabName) =>
          (tabName.slug ? tabName.slug : tabName.replace(/\s+/g, "-").toLowerCase()) === this.$route.params.tab
      );
      return this.currentSettings[i - 2];
    },
    currentTemplateLocaleOption() {
      return this.templateLocaleOption ? this.templateLocaleOption.value : "en";
    },
    templateSettings() {
      return this.currentTemplate.current_draft.config;
    },
    ...mapGetters({
      teams: "auth_get_teams",
      teamsSettings: "global_get_teams_settings",
      currentTemplate: "templates_get_current_template",
      currentTemplateMeta: "templates_get_current_template_meta",
      teamLanguages: "teams_get_languages",
      currentTemplateSnippets: "templates_get_current_template_snippets",
      statiBits: "global_get_fileStatiBits",
      templateLibraryFolders: "templates_get_template_library_folders",
      templateBuilderSession: "templates_get_template_builder_session",
    }),
  },
};
</script>
